import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { MessageCircle, Check, RotateCcw } from "lucide-react";

const DEFAULT_TEMPLATE = `Bonjour {capitaine},

🎉 BONNE NOUVELLE — URBAN FEST : NOUVELLE DATE !

L'Urban Fest aura lieu les 29 & 30 AOÛT 2026 au Quartier Jean-Yole, La Roche-sur-Yon.

L'inscription de votre équipe {equipe} ({discipline}) reste bien valable. Merci de confirmer votre présence à la nouvelle date par retour de message.

Pour toute question : 06 95 62 29 59 (WhatsApp).

À très vite !
L'équipe Urban Fest`;

const normalizePhone = (raw) => {
  const digits = (raw || "").replace(/\D/g, "");
  if (digits.startsWith("33")) return digits;
  if (digits.startsWith("0")) return "33" + digits.slice(1);
  return digits;
};

export default function WhatsAppPanel() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [template, setTemplate] = useState(
    () => localStorage.getItem("uf_wa_template_v2") || DEFAULT_TEMPLATE
  );
  const [sent, setSent] = useState(() => {
    try { return JSON.parse(localStorage.getItem("uf_wa_sent") || "{}"); } catch { return {}; }
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/teams");
      setTeams(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    localStorage.setItem("uf_wa_template_v2", template);
  }, [template]);

  const buildMessage = (t) =>
    template
      .replaceAll("{equipe}", t.name)
      .replaceAll("{capitaine}", t.captain_name || "")
      .replaceAll("{discipline}", t.discipline === "basket" ? "Basket 3v3" : "Foot 5v5");

  const send = (t) => {
    const phone = normalizePhone(t.captain_phone);
    if (!phone || phone.length < 11) {
      toast.error(`Numéro invalide pour ${t.name} : ${t.captain_phone || "aucun"}`);
      return;
    }
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(buildMessage(t))}`, "_blank");
    const next = { ...sent, [t.id]: true };
    setSent(next);
    localStorage.setItem("uf_wa_sent", JSON.stringify(next));
  };

  const resetSent = () => {
    setSent({});
    localStorage.removeItem("uf_wa_sent");
    toast.success("Suivi des envois réinitialisé");
  };

  if (loading) return <p className="text-white/50">Chargement…</p>;

  const filtered = teams.filter((t) => filter === "all" || t.status === filter);
  const sentCount = filtered.filter((t) => sent[t.id]).length;

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-whatsapp-panel">
      <div className="uf-card p-6">
        <h3 className="font-display text-2xl mb-1 text-[#25D366]">MESSAGE WHATSAPP</h3>
        <p className="text-xs text-white/50 mb-4">
          Variables disponibles : <code className="text-[#FF9F0A]">{"{capitaine}"}</code>,{" "}
          <code className="text-[#FF9F0A]">{"{equipe}"}</code>,{" "}
          <code className="text-[#FF9F0A]">{"{discipline}"}</code> — remplacées automatiquement pour chaque équipe.
        </p>
        <textarea
          className="uf-input w-full !h-80 font-mono text-sm leading-relaxed"
          value={template}
          onChange={(e) => setTemplate(e.target.value)}
          data-testid="wa-template-input"
        />
        <div className="flex gap-3 mt-3">
          <button
            type="button"
            onClick={() => setTemplate(DEFAULT_TEMPLATE)}
            className="text-xs uppercase tracking-widest border border-white/20 text-white/60 px-3 py-2 hover:text-white rounded-full transition-colors"
            data-testid="wa-template-reset-btn"
          >
            Rétablir le message par défaut
          </button>
        </div>
        <p className="text-xs text-white/40 mt-4 leading-relaxed">
          Cliquez sur « Envoyer » à côté d'une équipe : WhatsApp s'ouvre avec le message pré-rempli
          vers le capitaine, envoyé depuis <strong className="text-white/70">votre propre numéro (06 95 62 29 59)</strong>.
          Le capitaine peut vous répondre directement. Il ne reste qu'à appuyer sur le bouton d'envoi dans WhatsApp.
        </p>
      </div>

      <div className="uf-card overflow-hidden">
        <div className="p-4 flex items-center gap-2 flex-wrap border-b border-white/10 bg-black/40">
          {[
            { v: "all", label: `Toutes (${teams.length})` },
            { v: "approved", label: `Validées (${teams.filter((t) => t.status === "approved").length})` },
            { v: "pending", label: `En attente (${teams.filter((t) => t.status === "pending").length})` },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setFilter(f.v)}
              className={`text-xs uppercase tracking-widest px-3 py-1 rounded-full border transition-colors ${
                filter === f.v
                  ? "border-[#25D366] text-[#25D366]"
                  : "border-white/20 text-white/50 hover:text-white"
              }`}
              data-testid={`wa-filter-${f.v}`}
            >
              {f.label}
            </button>
          ))}
          <span className="ml-auto text-xs text-white/40">{sentCount}/{filtered.length} envoyés</span>
          <button
            onClick={resetSent}
            className="text-white/40 hover:text-white transition-colors"
            title="Réinitialiser le suivi"
            data-testid="wa-reset-sent-btn"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
        <div className="max-h-[520px] overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="text-center p-10 text-white/40">Aucune équipe.</p>
          ) : filtered.map((t) => (
            <div
              key={t.id}
              className="flex items-center gap-3 px-4 py-3 border-b border-white/5"
              data-testid={`wa-team-${t.id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="font-bold truncate">
                  {t.name}{" "}
                  <span className="text-xs font-normal uppercase text-white/40">
                    · {t.discipline === "basket" ? "Basket" : "Foot"}
                  </span>
                </p>
                <p className="text-xs text-white/50 truncate">
                  {t.captain_name} · {t.captain_phone || "— pas de téléphone —"}
                </p>
              </div>
              {sent[t.id] && <Check className="w-4 h-4 text-[#25D366] shrink-0" data-testid={`wa-sent-${t.id}`} />}
              <button
                onClick={() => send(t)}
                disabled={!t.captain_phone}
                className="shrink-0 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold bg-[#25D366] text-black px-3 py-2 rounded-full hover:brightness-110 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid={`wa-send-${t.id}`}
              >
                <MessageCircle className="w-4 h-4" />
                {sent[t.id] ? "Renvoyer" : "Envoyer"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
